import React, { useEffect, useRef, useState } from "react";
import { StewartDialog, StewartDialogActions } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { borderRadius, colors, fontSize, fontWeight, gradients, iconSize, margin, padding } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import { FileUploadStatus, Message } from "entities/UIModel";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import ViewContractIcon from "theme/icons/ViewContractIcon";
import CustomTooltip from "controls/global/custom-tooltip";
import ToggleOffIcon from "theme/icons/ToggleOffIcon";
import ToggleOnIcon from "theme/icons/ToggleOnIcon";
import FlashMessageV2 from "controls/global/flash-message/FlashMessageV2";
import { useFlashMessageV2Actions } from "utils/context/FlashMessageV2Context";
import { FlashMessageType, PERMISSIONS } from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(98vh)",
    maxWidth: "calc(40vw)",
    minWidth: "calc(40vw)",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 40px 0px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledDialogContent = styled(Box)({});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  paddingBottom: padding.xlarge22,
  paddingTop: padding.zero,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

interface MessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  message: (Message & { isToggled?: boolean }) | null;
  isToggled?: boolean;
  onToggle: (messageID: number, newIsHidden: number) => void;
}

const MessageDialog = ({ isOpen, onClose, message, isToggled, onToggle }: MessageDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { getValues } = useFormWrapper();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("Message hidden successfully.");
  const [, { showFlashMessage }] = useFlashMessageV2Actions();
  const [{ profileSettings }] = useProfileSettingsCache();

  const isProgressCompletedRef = useRef<boolean>(false);
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const [confirmationMessageDetail, setConfirmationMessageDetail] = useState(
    "Hiding messages will prevent them from appearing to external users, but they can be unhidden by using the unhide button.",
  );
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasHideUnHideAccess = permissionCodes?.includes(PERMISSIONS.TITLESEARCH_HIDEUNHIDEMESSAGES);

  const isFileDisabled = useFieldDisabler("AddTitleSearchButton");

  const internalFileId = useRef<number>(0);

  function handleOnClose() {
    onClose && onClose();
  }

  useEffect(() => {
    setOpen(isOpen);
    successUploadFilesRef.current = [];
    failedUploadFilesRef.current = [];
    isProgressCompletedRef.current = false;
    if (internalFileId.current) {
      internalFileId.current = getValues("id");
    }
  }, [isOpen]);

  const handleClickOnDone = () => {
    onClose && onClose();
  };

  const handleToggle = () => {
    if (message?.messageID) {
      const newIsHidden = isToggled ? 0 : 1;
      onToggle(message.messageID, newIsHidden);

      if (newIsHidden === 1) {
        setConfirmationMessage("Message hidden successfully.");
        setConfirmationMessageDetail(
          "Hiding messages will prevent them from appearing to external users, but they can be unhidden by using the unhide button.",
        );
        showFlashMessage(FlashMessageType.Warning, confirmationMessage, confirmationMessageDetail);
        }
      setShowConfirmation(true);
    }
  };

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle id="styledDialogTitleDebug" >
          <IconContainer>
            <ViewContractIcon />
          </IconContainer>
          <Typography id="uploadPolicyImageTitleDebug">View Full Message</Typography>
          <CloseIconContainer>
            <IconButton
              className="iconButton"
              onClick={handleOnClose}
            >
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>
        <Box
          sx={{
            border: `1px solid ${colors.grey07}`,
            borderRadius: borderRadius.medium,
            padding: padding.medium1,
            marginTop: margin.xlarge1,
            maxWidth: "100%",
            position: "relative",
            backgroundColor: colors.grey03,
            minHeight: "200px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-10px",
              height: "12px",
              color: colors.grey06,
              fontSize: fontSize.small,
              backgroundColor: "white",
              marginBottom: margin.zero,
              paddingBottom: padding.zero,
            }}
          >
            Message
          </Box>

          <Box
            sx={{
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              fontSize: fontSize.medium,
              color: colors.grey07,
            }}
          >
            {message?.messageText || "No message found."}
          </Box>
        </Box>

        {hasHideUnHideAccess && <StyledDialogContent style={{ display: "flex" }}>
          <CustomTooltip
            title={!isToggled ? "Hide Message" : "UnHide Message"}
            placement="top"
            arrow
          >
            <IconButton
              onClick={handleToggle}
              disabled={isFileDisabled}
              className="actionButton"
              sx={{ color: colors.blue01, marginLeft: "-4px", paddingLeft: "0px" ,height :"15px" ,width :"55px" , top:"18px"  }}
            >
              {isToggled ? <ToggleOffIcon /> : <ToggleOnIcon />}
            </IconButton>
          </CustomTooltip>
          {!isToggled ? (
            <div style={{ marginTop: margin.large, fontSize: fontSize.small1 }}>{"Hide Message"}</div>
          ) : (
            <div style={{ marginTop: margin.large, fontSize: fontSize.small1 }}>{"UnHide Message"}</div>
          )}
        </StyledDialogContent>}

        <StyledDialogAction className="DialogAction" style={{paddingTop :hasHideUnHideAccess ? "" :padding.large1 }}>
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleClickOnDone}
          >
            Close
          </ActionButton>
        </StyledDialogAction>
        <FlashMessageV2
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          message={confirmationMessage}
        />
      </StyledDialog>
    </>
  );
};

export default MessageDialog;
